/**
 * The visible contacts' stored S3 avatars, from CloudFront (`/media`) when the
 * media session serves avatars, presigned otherwise.
 *
 * Baby Chops stores each contact's avatar as a durable JPEG in S3, and the
 * import_worker persists that object's key on the row as
 * `profile_image_s3_key`. This hook presigns those keys (`POST
 * /avatars/presign`, one batch per render) so the table renders the durable S3
 * copy. The web deliberately never renders the contact's ORIGINAL
 * `profile_image_url` (a `media.licdn.com` link that expires ~2 weeks after
 * capture and 403s from the web origin) — avatars are S3-or-initials only.
 * Because it uses the durable key — not a LinkedIn-URL cache lookup — it works
 * past the 15-day avatar-cache TTL. A contact with no stored key simply isn't in
 * the map; the caller renders the initials monogram.
 */

import { useEffect, useMemo } from "react";

import type { ImportsClient } from "../../../clients/imports.client";
import { useStoredImageUrls } from "../../../features/media/useStoredImageUrls";
import { useVersionedKeys } from "../../../features/media/versionedKeys";

/** Anything that carries the durable stored-avatar key — an `ImportContactRow`
 *  (contacts table) or a `ConflictContact` (compare modal). */
interface HasStoredAvatarKey {
  profile_image_s3_key?: string | null;
  /** The key's capture time in epoch seconds, its CloudFront URL's `?v=`
   *  (`versionedKeys`). */
  profile_image_captured_at?: number | null;
}

/** `profile_image_s3_key` → short-lived presigned S3 GET URL, for contacts that
 *  carry a stored avatar key. Absent keys mean "no stored avatar — use the
 *  fallback". */
export function useResolveAvatars(
  api: ImportsClient | undefined,
  contacts: readonly HasStoredAvatarKey[]
): ReadonlyMap<string, string> {
  // De-duped, stable list of the visible contacts' durable avatar keys, each
  // with its capture time: a re-captured photo is rewritten under the same
  // key, and the version makes its CloudFront URL new.
  const { s3Keys, versions } = useVersionedKeys(
    contacts.map((c) => [c.profile_image_s3_key, c.profile_image_captured_at] as const)
  );

  // From CloudFront when the media session serves avatars (no request at all);
  // otherwise presigned through `POST /avatars/presign`, as before. The
  // presigned URLs live ~1h and are held 30 min, so the same visible contacts
  // do not re-hit `/avatars/presign` on page turns.
  const presign = useMemo(
    () => (api ? async (keys: string[]) => (await api.presignAvatars(keys)).urls : undefined),
    [api]
  );
  const { urls, error } = useStoredImageUrls(
    "avatars",
    s3Keys,
    presign,
    "avatar-presign",
    versions
  );

  // Fail-SOFT, not fail-silent. Avatars are cosmetic, so a presign error just
  // leaves the affected rows on their initials monogram — no thrown error, no
  // blocked table. But the signature failure this epic routes around is a silent
  // ALB-WAF 403 on an oversized batch, which renders identically to "no contact
  // has a stored avatar". Surface it once so an operator gets a one-line
  // diagnosis instead of chasing a phantom. (The client chunks batches to stay
  // under the WAF cap; this is the backstop if a 403 ever slips through.)
  useEffect(() => {
    if (error) {
      console.warn(
        `[useResolveAvatars] presign failed for ${s3Keys.length} key(s); those contacts render initials.`,
        error
      );
    }
  }, [error, s3Keys.length]);

  return urls;
}
